/**
 * D87 — "To book — coming back": jobs a tech left unfinished.
 *
 * A job left for parts or more time is not finished and not scheduled, which is
 * where it gets lost. It sits near the top of the office's landing screen, oldest
 * first as the server orders it, until a later visit is booked or someone arrives.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { daysSince, returnReasonLabel, waitingLabel } from "../lib/daywork";
import { Card, Chip } from "./ui";

export function ToBookCard() {
  const { can } = useAuth();
  const office = can(...OFFICE);
  const list = useQuery({
    queryKey: ["jobs", "to-book"],
    queryFn: () => api.jobs.toBook(),
    enabled: office,
  });

  if (!office) return null;

  const rows = list.data ?? [];

  return (
    <Card
      title="To book — coming back"
      subtitle={list.data ? `${rows.length} job${rows.length === 1 ? "" : "s"}` : undefined}
      padded={false}
    >
      {list.isPending ? (
        <p className="tiny muted tobook-note">Checking for jobs to book…</p>
      ) : list.error ? (
        <p className="tiny muted tobook-note">
          Couldn't check for jobs to book.{" "}
          <button type="button" className="linkish tiny tobook-retry" onClick={() => void list.refetch()}>
            Try again
          </button>
        </p>
      ) : rows.length === 0 ? (
        <p className="tiny muted tobook-note">No jobs are waiting for a return visit.</p>
      ) : (
        <ul className="tobook-list">
          {rows.map((row) => {
            const days = daysSince(row.left_site_at);
            return (
              <li key={row.job_id} className="tobook-row">
                <div className="tobook-main">
                  <Link to={`/jobs/${row.job_id}`} className="tobook-link mono">
                    {row.job_number}
                  </Link>
                  <span className="tobook-title">{row.title}</span>
                  <span className="tiny muted">
                    {row.site_name && row.site_name !== row.customer_name
                      ? `${row.customer_name} · ${row.site_name}`
                      : row.customer_name}
                  </span>
                  {row.return_note && <span className="tiny">{row.return_note}</span>}
                </div>
                <div className="tobook-side">
                  <Chip tone={row.return_reason === "parts" ? "warn" : "neutral"}>
                    {returnReasonLabel(row.return_reason)}
                  </Chip>
                  {row.return_hours ? <span className="tiny mono">{row.return_hours} h</span> : null}
                  {days !== null && <span className="tiny muted">{days === 0 ? "today" : waitingLabel(days)}</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
